import { FC } from 'react'
import { Box, List, ListItemButton, ListItemText, Paper, Typography } from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { xxllncColor } from '../../layout/themes'

export interface TreeNode {
  details: {
    code: string
    naam: string
    gwb?: number | string | null
    profiel_code?: string
    gi_code?: string
  }
  children?: Record<string, TreeNode>
  selected?: number
}

interface Props {
  treeview: Record<string, TreeNode>
  selected: string
  onSelect: (code: string) => void
}

interface FlatNode {
  code: string
  naam: string
  level: number
}

const flatten = (tree: Record<string, TreeNode>, level = 0, acc: FlatNode[] = []): FlatNode[] => {
  Object.values(tree).forEach((node) => {
    const details = node.details
    acc.push({ code: details.code, naam: details.naam, level })
    if (node.children && Object.keys(node.children).length > 0) {
      flatten(node.children, level + 1, acc)
    }
  })
  return acc
}

const OverigeDataTreeview: FC<Props> = ({ treeview, selected, onSelect }) => {
  const nodes = flatten(treeview)

  if (nodes.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Geen gebied-indeling beschikbaar.
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', maxHeight: 600, overflowY: 'auto' }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: xxllncColor.opDark10,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="overline"
          sx={{ fontWeight: 600, letterSpacing: 0.8, color: xxllncColor.dark100 }}
        >
          Gebieden ({nodes.length})
        </Typography>
      </Box>
      <List dense disablePadding>
        <ListItemButton selected={selected === ''} onClick={() => onSelect('')}>
          <ListItemText
            primary="Alle"
            primaryTypographyProps={{ fontWeight: 600, fontSize: 13 }}
          />
        </ListItemButton>
        {nodes.map((node) => (
          <ListItemButton
            key={`${node.code}-${node.level}`}
            selected={selected === node.code}
            onClick={() => onSelect(node.code)}
            sx={{ pl: 2 + node.level * 2 }}
          >
            <LocationOnIcon
              fontSize="small"
              sx={{ mr: 1, color: xxllncColor.orange100, fontSize: 14 }}
            />
            <ListItemText
              primary={`${node.code} — ${node.naam}`}
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </ListItemButton>
        ))}
      </List>
    </Paper>
  )
}

export default OverigeDataTreeview
